import { showConfirmModal } from '../../modules/modals/confirmService';
import { toNumber } from '../../utils/numbers';
import { DeviceStatus } from '../../constants/DeviceStatus';
import {
  RemoteBatchData,
  RemoteControlEventType,
} from '../../containers/Batch/RemoteBatchController';
import type { IBatchStore } from './batch-store-types';
import type { IPump } from '../../../types/device/DeviceModels';

/**
 * Check if a device is marked for discharge finish
 */
export function isFinishDischargeDevice(batchStore: IBatchStore, deviceId: number): boolean {
  return batchStore.finishDischargeDevices.includes(deviceId);
}

/**
 * Handle finish discharge button press
 * Shows a confirmation dialog with remaining discharge amount
 */
export function onFinishDischargeButtonPress(
  batchStore: IBatchStore,
  deviceId: number,
  onConfirmed: () => void = () => {},
): void {
  if (isFinishDischargeDevice(batchStore, deviceId)) {
    return;
  }

  let ds = batchStore.getDeviceStatus(deviceId);
  const missingTarget = toNumber((ds.target || 0) - (ds.weight || 0), 2);
  let message: string;

  if (missingTarget > 0) {
    message = `Discharge target for ${ds.materialCode} is ${ds.target} ${ds.unit}.
    You still have ${missingTarget} ${ds.unit} remaining to discharge.
    Are you sure you want to stop the discharge?`;
  } else {
    message = `Discharge target for ${ds.materialCode} is ${ds.target} ${ds.unit}.
    Discharge is over by ${missingTarget * -1} ${ds.unit}.
    Are you sure you want to stop the discharge?`;
  }

  showConfirmModal({
    title: 'Warning!',
    message: message,
    okLabel: 'Yes',
    cancelLabel: 'No',
    enableEscape: false,
    onExited: () => {},
  }).then(() => {
    ds = batchStore.getDeviceStatus(deviceId);
    if (
      !isFinishDischargeDevice(batchStore, deviceId) &&
      [DeviceStatus.DISCHARGING, DeviceStatus.DISCH_DELAY, DeviceStatus.LINE_CLEAR].includes(ds.status)
    ) {
      if (batchStore.canSendRemoteInstruction()) {
        batchStore.sendRemoteInstruction(
          RemoteBatchData(
            RemoteControlEventType.FINISH_DISCHARGE_DEVICE,
            null,
            { deviceId },
          ),
        );
        return;
      } else {
        batchStore.finishDischargeDevices.push(deviceId);
      }
      onConfirmed();
    }
  });
}

/**
 * Add finish discharge device to the list
 */
export function addFinishDischargeDevice(batchStore: IBatchStore, deviceId: number): void {
  if (!isFinishDischargeDevice(batchStore, deviceId)) {
    batchStore.finishDischargeDevices.push(deviceId);
  }
}

/**
 * Remove finish discharge device from the list
 */
export function removeFinishDischargeDevice(batchStore: IBatchStore, deviceId: number): void {
  batchStore.finishDischargeDevices = batchStore.finishDischargeDevices.filter(id => id !== deviceId);
}

/**
 * Check if pump discharge should be finished early
 * @returns true if discharge was finished
 */
export function checkPumpDischargeFinish(
  batchStore: IBatchStore,
  pump: IPump,
  weight: number,
  target: number,
  onFinish: () => void,
  reaction: { dispose: () => void },
): boolean {
  if (weight >= target || batchStore.hasPumpFinished(pump) || isFinishDischargeDevice(batchStore, pump.id)) {
    if (isFinishDischargeDevice(batchStore, pump.id)) {
      removeFinishDischargeDevice(batchStore, pump.id);
    }
    reaction.dispose();
    onFinish();
    return true;
  }
  return false;
}
